// El detalle de una propuesta V2 (useGetPropuestaV2Query) adjunta a cada ítem
// vigente su decisión vigente en `item.respuesta` (o `null` si el servicio
// todavía no tiene una) — con la misma forma que ya consume el portal
// público, salvo dos diferencias deliberadas: acá viene además la ronda de
// la decisión, y no viene el puente de compatibilidad que el portal conserva
// para una pestaña abierta con el bundle anterior. No "completes" el espejo
// agregando ese puente: la migración lo va a eliminar, no a extender.
// Esa fila sale de `propuesta_item_respuesta`, la única fuente del estado
// actual.
//
// A propósito NO se deriva de la bitácora del hilo: al enviar una ronda nueva
// el backend borra las decisiones no terminales para que el cliente vuelva a
// pronunciarse, pero la bitácora es append-only y las conserva. Derivar de
// ahí mostraría como observados o rechazados servicios que en realidad ya
// quedaron sin respuesta otra vez. El backend además rechaza (409) cualquier
// intento de reenviar una decisión sobre un ítem que ya quedó en ACEPTADO, así
// que quien arma el payload de aceptación interna necesita saber cuáles son
// esos ítems para excluirlos, no solo para deshabilitarlos visualmente.

/**
 * Decisión vigente por ítem, leída directamente de `item.respuesta`.
 *
 * @param {Array<{id: number, respuesta: {decision: string}|null}>} items ítems vigentes de la propuesta
 * @returns {Map<number, string>}
 */
export function derivarDecisionesPorItem(items) {
  const mapa = new Map();
  for (const item of items ?? []) {
    if (item?.respuesta?.decision) {
      mapa.set(item.id, item.respuesta.decision);
    }
  }
  return mapa;
}

/**
 * Cuenta cuántos ítems vigentes quedan aceptados y cuántos no, con la misma
 * regla que usa el backend para derivar el estado de cierre: lo observado sin
 * resolver y lo que nunca se respondió cuentan como no aceptado.
 *
 * @param {Array<{id: number}>} items ítems vigentes de la propuesta
 * @param {Map<number, string>} decisionesPorItem
 * @returns {{aceptados: number, noAceptados: number, total: number}}
 */
export function contarAceptadosVigentes(items, decisionesPorItem) {
  const total = items.length;
  const aceptados = items.filter(
    (item) => decisionesPorItem.get(item.id) === "ACEPTADO",
  ).length;
  return { aceptados, noAceptados: total - aceptados, total };
}

/**
 * Estado terminal anticipado si se cerrara la propuesta ahora, con la misma
 * regla que aplica el backend (derivarEstadoCierre): sin aceptados es
 * rechazo, todos aceptados es aceptación completa, cualquier otra
 * combinación es aceptación parcial. Es una anticipación para la interfaz —
 * el estado real siempre lo decide el backend en el momento del cierre.
 *
 * @param {number} aceptados
 * @param {number} total
 * @returns {"ACEPTADA"|"PARCIALMENTE_ACEPTADA"|"RECHAZADA"}
 */
export function anticiparEstadoCierre(aceptados, total) {
  if (aceptados === 0) return "RECHAZADA";
  if (aceptados === total) return "ACEPTADA";
  return "PARCIALMENTE_ACEPTADA";
}
